<template>
  <transition
    enter-active-class="transition duration-150 ease-in-out"
    enter-from-class="opacity-0 transform-translate-x-20"
    enter-to-class="opacity-100"
    leave-active-class="transition duration-150 ease-in-out"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0 transform -translate-x-20"
  >
    <aside
      v-show="isOpen"
      class="hidden overflow-y-auto z-50 flex-shrink-0 w-64 border-r border-primary-950 md:block bg-primary-900 dark:bg-primary-950"
      tabindex="0"
      @keyup.esc="closeSidebar"
    >
      <div class="pb-4 text-gray-500 dark:text-gray-400">
        <div class="flex gap-4 items-center py-4">
          <NuxtLink
            class="ml-6 text-lg font-bold text-gray-200 dark:text-gray-200"
            to="/"
          >
            OCV
          </NuxtLink>
          <ColorSchemeSwitcher />
        </div>
        <PanelMenu
          v-model:expanded-keys="expandedKeys"
          :model="menuItems"
          :pt="{
            headerContent: {
              class: 'bg-transparent text-white ',
            },
            panel: {
              class:
                'relative px-6 py-3 border-none bg-transparent dark:bg-transparent hover:bg-[var(--ocv-secondary-soft)]',
            },
            rootList: {
              class:
                'p-2 mx-auto space-y-2 overflow-hidden text-sm font-medium rounded-md border-none shadow-inner bg-primary-950/60 dark:text-gray-400 dark:bg-primary-950',
            },
            menuItem: {
              class: 'px-2 py-1',
            },
            content: {
              class: '',
            },
            itemContent: {
              class:
                'transition-colors duration-150 text-gray-200 hover:bg-[var(--ocv-secondary-soft)] dark:hover:text-[var(--ocv-secondary-500)]',
            },
          }"
          @panel-open="onPanelOpen"
          @panel-close="onPanelClose"
        >
          <template #item="{ item, active }">
            <SidebarItem
              :ref="(el) => item && item.key && (panelItemsRef[item.key] = el)"
              :item="item"
              v-bind="item?.attrs"
              :is-active="active"
              :selected="selectedItem?.key == item.key"
              @update:selected="onItemSelected"
            />
          </template>
        </PanelMenu>
      </div>
    </aside>
  </transition>
</template>

<script setup lang="ts">
import type { MenuItem } from "primevue/menuitem";
import Role from "~/types/Role";

defineOptions({
  name: "AppSidebar",
});

type AccessMenuItem = MenuItem & {
  key?: string;
  roles?: string[];
  permissions?: string[];
  items?: AccessMenuItem[];
};

const sidebarStore = useSidebarStore();
const userPermissions = usePermissions();
const userRoles = useRoles();
const expandedKeys = ref({});
const panelItemsRef = shallowReactive<Record<string, unknown>>({});
const { isOpen } = storeToRefs(sidebarStore);
const { closeSidebar } = sidebarStore;
const selectedItem = shallowRef({});
const { menuItems: items = [] } = defineProps<{
  menuItems?: Array<AccessMenuItem>;
}>();
const menuItems = ref([...items]);
interface Node {
  key: string;
  items: Node[];
}
function onPanelOpen(item: { item: string }) {
  sidebarStore.addOpenedPanel(item.item);
}

function onPanelClose(item: { item: string }) {
  sidebarStore.removeOpenedPanel(item.item);
}

const expandNode = (
  node: Node,
  expandedKeys: { value: { [key: string]: boolean } },
) => {
  if (node.items && node.items.length) {
    expandedKeys.value[node.key] = true;
    for (const child of node.items) {
      expandNode(child, expandedKeys);
    }
  }
};
const filterMenuItemsByAccessRights = (
  menuItems: AccessMenuItem[],
  userRolesWithPermissions: { roles: string[]; permissions: string[] },
) => {
  return menuItems.reduce<AccessMenuItem[]>((newMenuItem, currentMenuItem) => {
    const shouldCheckRoleAccess = currentMenuItem.roles != undefined;
    const { roles: userRoles, permissions: userPermissions }
      = userRolesWithPermissions;
    const rolesByPassingAll = [Role.SUPER_ADMIN];
    const userHasAllRight
      = rolesByPassingAll?.filter(role => userRoles.includes(role))?.length > 0;
    const shouldCheckPermissionAccess
      = currentMenuItem.permissions != undefined;
    if (
      (!shouldCheckPermissionAccess && !shouldCheckPermissionAccess)
      || userHasAllRight
    ) {
      newMenuItem.push(currentMenuItem);
      return newMenuItem;
    }

    if (shouldCheckRoleAccess) {
      const userHasMatchingRoles
        = currentMenuItem?.roles?.filter(role => userRoles.includes(role))
          ?.length > 0;
      if (userHasMatchingRoles) {
        if (currentMenuItem.items) {
          currentMenuItem.items = filterMenuItemsByAccessRights(
            currentMenuItem.items,
            userRolesWithPermissions,
          );
        }

        newMenuItem.push(currentMenuItem);
        return newMenuItem;
      }
    }
    if (shouldCheckPermissionAccess) {
      const userHasMatchingPermissions
        = currentMenuItem?.permissions?.filter(permission =>
          userPermissions.includes(permission),
        )?.length > 0;
      if (userHasMatchingPermissions) {
        if (currentMenuItem.items) {
          currentMenuItem.items = filterMenuItemsByAccessRights(
            currentMenuItem.items,
            userRolesWithPermissions,
          );
        }
        newMenuItem.push(currentMenuItem);
        return newMenuItem;
      }
    }
    return newMenuItem;
  }, []);
};

onMounted(() => {
  sidebarStore.getOpenedPanels.forEach((item: Node) => {
    expandNode(item, expandedKeys);
  });
  selectedItem.value = sidebarStore.getSelectedItem();
  const userAccessRight = {
    roles: userRoles.value,
    permissions: userPermissions.value,
  };
  menuItems.value = filterMenuItemsByAccessRights(
    menuItems.value,
    userAccessRight,
  );
});
async function onItemSelected(item: { href: string; key: string }) {
  selectedItem.value = item;
  sidebarStore.setSelectedItem(item);
}
</script>

<style scoped>
:deep(.panelmenu.item.focus.color) {
  color: --color-primary-500;
}
</style>
